import { groupBy, isNumber, isObject, isString } from "lodash";
import { getFormatSql } from "src/utils/lazy-sql-formatter";
import formatXml from "xml-formatter";
import {
  SyncFailedWithRejectedRowsError,
  type SyncRequestErrorInfo,
} from "src/types/sync-errors";
import type {
  RequestInfo as ApiRequestInfo,
  SyncAttemptFragment,
  SyncRequestFragment,
} from "src/graphql/types";
import { type Schedule, ScheduleType } from "src/components/schedule/types";
import pluralize from "pluralize";
import { MonitorStatus } from "@hightouch/lib/resource-monitoring/types";
import { DiffModeOverrideReason } from "@hightouch/lib/sync/diff-mode/types";
import { enumOrFallback, isEnum } from "src/types/utils";
import type { StatusIndicatorProps } from "@hightouch/htui";
import JSONBig from "json-bigint";
import { type Static, Type } from "@sinclair/typebox";
import { isPresent } from "ts-extras";
import type { LanguageSupport } from "@codemirror/language";

export const getSyncHref = (
  syncId: string,
  syncType: string | null | undefined,
): string =>
  `/${syncType === "realtime_audience_sync" ? "realtime-syncs" : "syncs"}/${syncId}`;

export const getSyncDisplayName = (
  model: { name?: string | null } | null | undefined,
  destination:
    | { name?: string | null; definition?: { name?: string | null } | null }
    | null
    | undefined,
): string =>
  `${model?.name ?? "Private model"} to ${
    destination?.name ?? destination?.definition?.name ?? "private destination"
  }`;

export enum SyncRunStatus {
  // These are definitely in use
  SUCCESS = "success",
  CANCELED = "cancelled",
  FAILED = "failed",
  WARNING = "warning",
  REPORTING = "reporting",
  PROCESSING = "processing",
  QUERYING = "querying",
  ENQUEUED = "queued",

  STREAMING = "streaming", // Used for realtime audience syncs

  DISABLED = "disabled", // Can a sync _run_ be disabled?
  PENDING = "pending", // Can a sync _run_ be pending?

  // This is used to indicate that a sync has never run, but only exists on the frontend
  NEVER_RUN = "never-run",

  // Are these still used?
  ABORTED = "aborted",
  PREPARING = "preparing-plan",
  IN_PROGRESS = "inprogress",
  INCOMPLETE = "incomplete",
  ACTIVE = "active",
  INTERRUPTED = "interrupted",

  /**
   * @deprecated Use SyncStatus.FAILED with a syncRequestErrorCode of PREVIOUS_SYNC_RUN_OBJECT_MISSING or WAREHOUSE_TABLE_MISSING.
   */
  UNPROCESSABLE = "unprocessable",
}

export const isSyncRunStatus = isEnum(SyncRunStatus);

export const UnknownSyncRunStatus = "unknown-status" as const;
export type UnknownSyncRunStatus = typeof UnknownSyncRunStatus;

export const syncRunStatusOrUnknownStatus = enumOrFallback(
  SyncRunStatus,
  UnknownSyncRunStatus,
  true,
);

export type SyncRunStatusOrUnknownStatus = ReturnType<
  typeof syncRunStatusOrUnknownStatus
>;

const RESYNC_REASON_LABELS: Partial<Record<DiffModeOverrideReason, string>> = {
  [DiffModeOverrideReason.ExplicitRequest]: "Triggered by user",
  [DiffModeOverrideReason.AddedFields]: "Fields were added",
  [DiffModeOverrideReason.ChangedConfig]: "Configuration was changed",
  [DiffModeOverrideReason.ChangedMappings]: "Mappings changed",
  [DiffModeOverrideReason.ChangedSourceTypes]: "Source types changed",
  [DiffModeOverrideReason.ChangedPrimaryKey]: "Model primary key was changed",
  [DiffModeOverrideReason.RetryFailedResync]: "Retrying failed resync",
  [DiffModeOverrideReason.ArchiveModeResync]: "Resyncing for archive mode",
  [DiffModeOverrideReason.NextDiffModeOverride]: "Next sync run override",
  [DiffModeOverrideReason.ResyncInterval]: "Scheduled refresh",
};

// Unlabeled reasons render as the raw value, so a new one only needs an entry
// above once it is worth spelling out.
export const resyncReasonLabel = (reason: string): string =>
  RESYNC_REASON_LABELS[reason as DiffModeOverrideReason] ?? reason;

/**
 * Returns true if the specified SyncStatus is terminal, i.e. it will never
 * transition to another status.
 */
export const syncStatusIsTerminal = (status: SyncRunStatus): boolean => {
  switch (status) {
    case SyncRunStatus.DISABLED:
    case SyncRunStatus.SUCCESS:
    case SyncRunStatus.ABORTED:
    case SyncRunStatus.CANCELED:
    case SyncRunStatus.FAILED:
    // A "warning" status is only produced by the backend for a run that has
    // already finished (it requires finished_at to be set, with some rejected
    // rows). It is terminal and will never transition to another status.
    case SyncRunStatus.WARNING:
      return true;
    default:
      return false;
  }
};

export const getSyncStatusColor = (
  status:
    | SyncRunStatus
    | SyncHealthStatus
    | UnknownSyncHealth
    | UnknownSyncRunStatus,
): string => {
  switch (status) {
    case SyncRunStatus.SUCCESS:
    case SyncHealthStatus.Healthy:
      return "green.500";
    case SyncRunStatus.WARNING:
    case SyncHealthStatus.Warning:
      return "yellow.500";
    case SyncRunStatus.FAILED:
    case SyncHealthStatus.Unhealthy:
      return "red.500";
    case SyncRunStatus.DISABLED:
    case SyncRunStatus.CANCELED:
    case SyncRunStatus.PENDING:
    case SyncRunStatus.ENQUEUED:
    case SyncRunStatus.NEVER_RUN:
    case SyncHealthStatus.Disabled:
    case SyncHealthStatus.Pending:
    case UnknownSyncHealth:
    case UnknownSyncRunStatus:
      return "base.border";
    case SyncRunStatus.REPORTING:
    case SyncRunStatus.PROCESSING:
    case SyncRunStatus.QUERYING:
    case SyncRunStatus.ABORTED:
    case SyncRunStatus.PREPARING:
    case SyncRunStatus.IN_PROGRESS:
    case SyncRunStatus.INCOMPLETE:
    case SyncRunStatus.ACTIVE:
    case SyncRunStatus.INTERRUPTED:
    case SyncRunStatus.UNPROCESSABLE:
    case SyncRunStatus.STREAMING:
      return "indigo.base";
  }
};

export const syncRunStatusToIndicatorVariant = (
  status: SyncRunStatus,
): StatusIndicatorProps["variant"] => {
  switch (status) {
    case SyncRunStatus.SUCCESS:
      return "success" as const;
    case SyncRunStatus.WARNING:
      return "warning" as const;
    case SyncRunStatus.FAILED:
    case SyncRunStatus.ABORTED:
    case SyncRunStatus.UNPROCESSABLE:
      return "error" as const;
    case SyncRunStatus.CANCELED:
    case SyncRunStatus.PENDING:
    case SyncRunStatus.INTERRUPTED:
    case SyncRunStatus.DISABLED:
    case SyncRunStatus.INCOMPLETE:
    case SyncRunStatus.NEVER_RUN:
      return "inactive" as const;
    case SyncRunStatus.ENQUEUED:
    case SyncRunStatus.QUERYING:
    case SyncRunStatus.PROCESSING:
    case SyncRunStatus.REPORTING:
    case SyncRunStatus.ACTIVE:
    case SyncRunStatus.IN_PROGRESS:
    case SyncRunStatus.PREPARING:
    case SyncRunStatus.STREAMING:
      return "processing" as const;
  }
};

export const syncRunStatusToDescription: {
  [k in SyncRunStatus]: string;
} = {
  [SyncRunStatus.DISABLED]: "Sync is disabled",
  [SyncRunStatus.PENDING]: "Sync is pending",
  [SyncRunStatus.PREPARING]: "Sync is preparing",
  [SyncRunStatus.QUERYING]: "Sync is querying",
  [SyncRunStatus.REPORTING]: "Sync is reporting",
  [SyncRunStatus.PROCESSING]: "Sync is processing",
  [SyncRunStatus.ENQUEUED]: "Sync is queued",
  [SyncRunStatus.ACTIVE]: "Sync is active",
  [SyncRunStatus.SUCCESS]: "Last run completed successfully",
  [SyncRunStatus.CANCELED]: "Last run canceled",
  [SyncRunStatus.FAILED]: "Last run failed",
  [SyncRunStatus.WARNING]: "Last run had errors",
  [SyncRunStatus.INTERRUPTED]: "Last run interrupted",
  [SyncRunStatus.ABORTED]: "Last run was aborted",
  [SyncRunStatus.INCOMPLETE]: "Last run incomplete",
  [SyncRunStatus.IN_PROGRESS]: "Sync is in progress",
  [SyncRunStatus.NEVER_RUN]: "Sync has never run",
  [SyncRunStatus.UNPROCESSABLE]: "Last run was unprocessable",
  [SyncRunStatus.STREAMING]: "Sync is streaming",
};

export const SyncStatusToText = {
  [SyncRunStatus.DISABLED]: "Disabled",
  [SyncRunStatus.PENDING]: "Pending",
  [SyncRunStatus.SUCCESS]: "Healthy",
  [SyncRunStatus.QUERYING]: "Querying",
  [SyncRunStatus.REPORTING]: "Reporting",
  [SyncRunStatus.PROCESSING]: "Processing",
  [SyncRunStatus.CANCELED]: "Canceled",
  [SyncRunStatus.FAILED]: "Failed",
  [SyncRunStatus.WARNING]: "Warning",
  [SyncRunStatus.INTERRUPTED]: "Interrupted",
  [SyncRunStatus.ENQUEUED]: "Queued",
  [SyncRunStatus.ACTIVE]: "Active",
  [SyncRunStatus.NEVER_RUN]: "Never run",
  [SyncRunStatus.STREAMING]: "Streaming",
};

export const ActiveSyncStatuses = [
  SyncRunStatus.QUERYING,
  SyncRunStatus.PROCESSING,
  SyncRunStatus.REPORTING,
  SyncRunStatus.ENQUEUED,
] as const;

export type ActiveSyncStatus = (typeof ActiveSyncStatuses)[number];

export function isActiveSyncStatus(
  status: SyncRunStatus,
): status is ActiveSyncStatus {
  return (ActiveSyncStatuses as readonly SyncRunStatus[]).includes(status);
}

/**
 * This structure sidesteps the issues that come out of using enums to populate another enum,
 * so that we can talk about SyncHealthStatus on its own terms rather than as a combination of monitor/pending state.
 */
export const SyncHealthStatus = {
  Healthy: MonitorStatus.Healthy,
  Unhealthy: MonitorStatus.Unhealthy,
  Warning: MonitorStatus.Warning,
  Disabled: MonitorStatus.Disabled,
  Pending: "pending",
} as const;

/**
 * Combine the states a monitor can be in with the states we show for overall sync health.
 */
export type SyncHealthStatus =
  (typeof SyncHealthStatus)[keyof typeof SyncHealthStatus];

export const isSyncHealthStatus = (
  status: string,
): status is SyncHealthStatus =>
  Object.values(SyncHealthStatus).includes(status as any);

export const UnknownSyncHealth = "unknown-health" as const;
export type UnknownSyncHealth = typeof UnknownSyncHealth;

export const syncHealthOrUnknown = enumOrFallback(
  SyncHealthStatus,
  UnknownSyncHealth,
  true,
);

export type SyncHealthOrUnknown = ReturnType<typeof syncHealthOrUnknown>;

export const getSyncRunOperations = ({
  attempt,
  syncRequest,
  queryRun,
}: {
  attempt?: Pick<SyncAttemptFragment, "error"> | null;
  syncRequest?: Pick<
    SyncRequestFragment,
    | "status_computed"
    // Note: this must use *_executed fields, and not *_checkpoint fields
    // *_executed fields are defined by a pg function that handles special casing `sync_request.sync_behavior`
    | "add_executed"
    | "change_executed"
    | "remove_executed"
    | "computed_progress"
    | "error"
    | "planner_type"
  > | null;
  queryRun?: Pick<NonNullable<SyncRequestFragment["query_run"]>, "size"> | null;
}): {
  successful: {
    added: number;
    changed: number;
    removed: number;
    total: number;
  };
  rejected: {
    added: number;
    changed: number;
    removed: number;
    total: number;
  };
} => {
  if (syncRequest?.planner_type === "all") {
    const isTerminal =
      isSyncRunStatus(syncRequest?.status_computed) &&
      syncStatusIsTerminal(syncRequest.status_computed);

    // No operations if a planner type all sync is still running
    if (!isTerminal) {
      return {
        successful: {
          added: 0,
          changed: 0,
          removed: 0,
          total: 0,
        },
        rejected: {
          added: 0,
          changed: 0,
          removed: 0,
          total: 0,
        },
      };
    }

    const hasError = Boolean(attempt?.error || syncRequest?.error);
    const querySize = queryRun?.size ?? 0;

    // Note: planner_type "all" sync run operations are _only_ counted as add operations.
    // On a hard error the whole run is rejected, since per-row counts may never have
    // been checkpointed for a failed all-mode run.
    if (hasError) {
      return {
        successful: { added: 0, changed: 0, removed: 0, total: 0 },
        rejected: {
          added: querySize,
          changed: 0,
          removed: 0,
          total: querySize,
        },
      };
    }

    // A terminal all-mode run without a hard error can still have partial rejections
    // (status "warning") — e.g. destinations that record rejected rows after the attempt
    // such as SFMC mirror mode. Account for those rejections instead of assuming an
    // all-or-nothing outcome (which previously reported them as fully successful, and
    // reported 0/0 entirely while the "warning" status was mistakenly treated as
    // non-terminal). Rejections are summed across op types so archive-mode removes are
    // captured too.
    const rejectedCount =
      (syncRequest?.computed_progress?.add_rejected_count ?? 0) +
      (syncRequest?.computed_progress?.change_rejected_count ?? 0) +
      (syncRequest?.computed_progress?.remove_rejected_count ?? 0);
    const successfulCount = Math.max(querySize - rejectedCount, 0);

    return {
      successful: {
        added: successfulCount,
        changed: 0,
        removed: 0,
        total: successfulCount,
      },
      rejected: {
        added: rejectedCount,
        changed: 0,
        removed: 0,
        total: rejectedCount,
      },
    };
  }

  const addRejected = syncRequest?.computed_progress?.add_rejected_count ?? 0;
  const changeRejected =
    syncRequest?.computed_progress?.change_rejected_count ?? 0;
  const removeRejected =
    syncRequest?.computed_progress?.remove_rejected_count ?? 0;

  const added = (syncRequest?.add_executed ?? 0) - addRejected;
  const changed = (syncRequest?.change_executed ?? 0) - changeRejected;
  const removed = (syncRequest?.remove_executed ?? 0) - removeRejected;

  return {
    successful: {
      added,
      changed,
      removed,
      total: added + changed + removed,
    },
    rejected: {
      added: addRejected,
      changed: changeRejected,
      removed: removeRejected,
      total: addRejected + changeRejected + removeRejected,
    },
  };
};

const DEPRECATED_ERROR = SyncFailedWithRejectedRowsError.MESSAGE;

export const isDeprecatedSyncRunError = (error: string): boolean =>
  [DEPRECATED_ERROR, "Error: " + DEPRECATED_ERROR].includes(error);

export const getSyncRunError = ({
  attempt,
  syncRequest,
}: {
  attempt?: Pick<SyncAttemptFragment, "error">;
  syncRequest?: Pick<SyncRequestFragment, "error">;
}): SyncRequestErrorInfo | undefined => {
  // Use `sync_requests.error` if present, then fall back to `sync_attempts.error`.
  const error: SyncRequestErrorInfo | undefined =
    syncRequest?.error ??
    (attempt?.error ? { message: attempt.error } : undefined);

  return error?.message && isDeprecatedSyncRunError(error.message)
    ? undefined
    : error;
};

/**
 * getObjectName returns a human friendly name for a synced object.
 **/
export function getObjectName(
  objectVal: string | undefined,
): string | undefined {
  if (!objectVal) {
    return objectVal;
  }

  const salesforceMultiOptions = [
    {
      label: "Contact or Lead",
      value: "___hightouch-reserved-contact-or-lead",
    },
    {
      label: "Account or Lead",
      value: "___hightouch-reserved-account-or-lead",
    },
  ];

  // We use a special identifier for Salesforce multitypes (e.g. Contact or Lead).
  const sfMultiType = salesforceMultiOptions.find(
    (mt) => mt.value === objectVal,
  );
  if (sfMultiType != null) {
    return sfMultiType.label;
  }

  return objectVal;
}

export const RequestInfoSchema = Type.Object({
  requestType: Type.String(),
  status: Type.String(),
  method: Type.String(),
  meta: Type.Any(),
  destination: Type.String(),
  requestBody: Type.String(),
  requestLanguage: Type.Any(),
  responseLanguage: Type.Any(),
  requestHeaders: Type.Record(Type.String(), Type.Any()),
  responseBody: Type.String(),
  errored: Type.Boolean(),
});

export type RequestLanguage =
  | "sql"
  | "json"
  | "xml"
  | "liquid"
  | "javascript"
  | LanguageSupport;

export type RequestInfo = Static<typeof RequestInfoSchema>;

const BATCH_REQUEST_INFO_DEFAULT_STATUS = "Success";
const BATCH_REQUEST_INFO_DEFAULT_DESTINATION = "Unknown destination";
const BATCH_REQUEST_INFO_DEFAULT_METHOD = "Save";

export const isXml = (value: unknown): boolean => {
  if (typeof value !== "string") {
    return false;
  }
  try {
    formatXml(value);
    return true;
  } catch (_err) {
    return false;
  }
};

// Test a string to see if it is SQL-like
export const isSql = (input: string): boolean => {
  if (!input || typeof input !== "string") {
    return false;
  }

  return [
    /\b(select|insert|update|delete|create|alter|drop|truncate)\b/,
    /\b(use|schema|table|show|remove)\b/,
    /\b(from|join|where|group by|order by|having|in|between|like)\b/,
  ].some((pattern) => pattern.test(input.toLowerCase()));
};

export const processRequestInfo = (
  requestInfo: ApiRequestInfo,
  definition?: { name: string },
): RequestInfo => {
  let requestBody: RequestInfo["requestBody"] = "";
  let requestLanguage: RequestLanguage | undefined;
  let meta: RequestInfo["meta"];
  let responseBody: RequestInfo["responseBody"] = "";
  let responseLanguage: RequestLanguage | undefined;
  let errored = false;
  let status = BATCH_REQUEST_INFO_DEFAULT_STATUS;
  let method = BATCH_REQUEST_INFO_DEFAULT_METHOD;
  let destination = BATCH_REQUEST_INFO_DEFAULT_DESTINATION;
  let requestHeaders: RequestInfo["requestHeaders"] = {};

  const data = requestInfo.data;

  try {
    let request =
      requestInfo.requestType === "method-call"
        ? data.parameters
        : data.request?.body;
    if (typeof request === "string") {
      try {
        request = JSONBig.parse(request);
      } catch {
        // Response is not json, ignore error and continue
      }
    }

    requestLanguage = isObject(request)
      ? "json"
      : isXml(request)
        ? "xml"
        : isSql(request)
          ? "sql"
          : undefined;
    requestBody =
      requestLanguage === "json"
        ? JSONBig.stringify(request, null, 2)
        : requestLanguage === "xml"
          ? formatXml(request)
          : requestLanguage === "sql"
            ? (getFormatSql()?.(request, { keywordCase: "upper" }) ?? request)
            : String(request ?? "");

    let response =
      requestInfo.requestType === "method-call"
        ? data.result
        : data.response?.body;
    if (typeof response === "string") {
      try {
        response = JSONBig.parse(response);
      } catch {
        // Response is not json, ignore error and continue
      }
    }
    responseLanguage = isObject(response)
      ? "json"
      : isXml(response)
        ? "xml"
        : undefined;
    responseBody =
      responseLanguage === "json"
        ? JSONBig.stringify(response, null, 2)
        : responseLanguage === "xml"
          ? formatXml(response)
          : !isPresent(response) && data.error
            ? JSONBig.stringify(data.error, null, 2)
            : String(response ?? "");

    if (data.method) {
      method = data.method;
    }
    if (data.error) {
      errored = true;
    }

    const error = data.response?.error;
    if (
      requestInfo.requestType !== "method-call" &&
      typeof error === "string"
    ) {
      if (error?.includes("TIMEDOUT")) {
        status = "TIMEOUT ERROR";
      } else {
        errored = true;
      }
    }
    if (data.response?.status && isString(data.response?.status)) {
      status = data.response?.status;
    }
    if (data.request?.method) {
      method = data.request?.method;
    }
    if (data.request?.url) {
      destination = data.request?.url;
    }
    if (data.request?.headers) {
      requestHeaders = data.request?.headers;
    }

    if (data.meta) {
      meta = data.meta;
    }
  } catch (_err) {
    // Fall back to default empty values
  }

  if (isNumber(data.response?.status)) {
    errored = data.response.status >= 400;
    status = errored
      ? `${data.response.status} ERR`
      : `${data.response.status} OK`;
  }

  if (
    destination === BATCH_REQUEST_INFO_DEFAULT_DESTINATION &&
    definition?.name
  ) {
    destination = definition.name;
  }

  if (status === BATCH_REQUEST_INFO_DEFAULT_STATUS && errored) {
    status = "Error";
  }

  return {
    ...requestInfo,
    requestBody,
    requestLanguage,
    requestHeaders,
    responseBody,
    responseLanguage,
    meta,
    errored,
    status,
    method,
    destination,
  };
};

export type MinimalSync = {
  id: string;
  description: string | null;
  destination: {
    definition: {
      icon: string | undefined;
      name: string | undefined;
    };
    name: string | null | undefined;
  } | null;
  status: string | null;
  sync_template?: { name: string } | null;
  sync_template_id?: number | null;
};

export type DisambiguatedSync = MinimalSync & { name: string };

const getSyncName = (sync: MinimalSync): string => {
  return (
    sync?.destination?.name ??
    sync?.destination?.definition?.name ??
    String(sync.id)
  );
};

/**
 * Syncs don't have names. We can instead identify them by their destination's name.
 * However, if there are multiple syncs with the same destination, we may need to disambiguate them.
 * In such a case, we append the sync ID to the destination name.
 */
export const disambiguateSyncs = (
  syncs: MinimalSync[],
): DisambiguatedSync[] => {
  const syncsGroupedByDestinationName = groupBy(syncs, (sync) =>
    getSyncName(sync),
  );

  return syncs.map((sync) => {
    const syncName = getSyncName(sync);
    const destinationHasMultipleSyncs =
      (syncsGroupedByDestinationName[syncName] || []).length > 1;

    return {
      ...sync,
      name: destinationHasMultipleSyncs
        ? `${syncName} (Sync #${sync.id})`
        : syncName,
    };
  });
};

export const describeWhenSyncRuns = (
  schedule: Schedule | null,
  { isEffectivelyDisabled = false }: { isEffectivelyDisabled?: boolean } = {},
): string => {
  switch (schedule?.type) {
    case undefined:
    case ScheduleType.MANUAL:
      return "Not scheduled";
    case ScheduleType.STREAMING:
      return "Runs continuously";
    case ScheduleType.CUSTOM:
      return "Runs on custom schedule";
    case ScheduleType.CRON:
      return `Runs on cron schedule (${schedule.schedule?.expression})`;
    case ScheduleType.FIVETRAN:
      return "Runs after Fivetran sync";
    case ScheduleType.DBT_CLOUD:
      return "Runs after dbt job";
    case ScheduleType.MATCH_BOOSTER:
      return "Runs after Match Booster";
    case ScheduleType.JOURNEY_TRIGGERED:
      // A journey_triggered sync is effectively disabled only when no running journey contains it.
      return isEffectivelyDisabled
        ? "Disabled because the journey is off"
        : "Runs when journey is triggered";
    case ScheduleType.INTERVAL:
      return schedule.schedule?.interval?.unit != null &&
        schedule.schedule?.interval.quantity != null
        ? `Runs every ${
            schedule.schedule?.interval?.quantity === 1
              ? schedule.schedule?.interval.unit
              : pluralize(
                  schedule.schedule?.interval?.unit,
                  schedule.schedule?.interval?.quantity,
                  true,
                )
          }`
        : "Not scheduled";
    default:
      return "Runs on schedule";
  }
};
